/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { format, parseISO } from 'date-fns';
import { type ReactNode, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { type Proforma } from '../../lib/api/proformas.js';
import { formatInr } from '../../lib/utils/money.js';
import { Badge, type BadgeTone } from '../ui/badge.js';

const STATUS_TONE: Readonly<Record<Proforma['status'], BadgeTone>> = {
  draft: 'slate',
  sent: 'amber',
  approved: 'green',
  declined: 'rose',
  converted: 'violet',
};

interface CardProps {
  readonly proforma: Proforma;
  readonly showClient?: boolean;
  readonly children?: ReactNode;
}

/** A proforma summary card (CR-008) with a slot for role-specific actions. */
export function ProformaCard({ proforma, showClient, children }: CardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-slate-800 dark:text-white">
              {proforma.proformaNumber}
            </p>
            <Badge
              label={t(`proforma.status.${proforma.status}`)}
              tone={STATUS_TONE[proforma.status]}
            />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {format(parseISO(proforma.periodStart), 'MMM yyyy')}
            {showClient === true ? ` · ${proforma.clientName}` : ''} ·{' '}
            {t('proforma.sessionsCount', { count: proforma.sessionsCount })}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-slate-800 dark:text-white">
            {formatInr(proforma.totalPaise)}
          </p>
          <p className="text-xs text-slate-400">
            {t('proforma.gstNote', {
              subtotal: formatInr(proforma.subtotalPaise),
              gst: formatInr(proforma.gstPaise),
            })}
          </p>
          {proforma.discountPaise > 0 && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400">
              {t('proforma.discountNote', { amount: formatInr(proforma.discountPaise) })}
            </p>
          )}
        </div>
      </div>
      {proforma.declineReason !== null && proforma.status === 'declined' && (
        <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
          {t('proforma.declinedNote', { reason: proforma.declineReason })}
        </p>
      )}
      {proforma.status === 'converted' && proforma.invoiceNumber !== null && (
        <p className="mt-2 rounded-lg bg-violet-50 px-3 py-2 text-xs text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
          {t('proforma.convertedNote', { number: proforma.invoiceNumber })}
        </p>
      )}
      {children !== undefined && (
        <div className="mt-3 flex flex-wrap justify-end gap-2">{children}</div>
      )}
    </div>
  );
}
